import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
  FormControl,
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { BackButtonComponent } from '@app/shared/components/back-button/back-button.component';
import { ActivatedRoute } from '@angular/router';
import { GrupoPermissoesService } from '../../api/services/grupo-permissoes.service';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { Toast, ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';

interface RoleDto {
  id: string;
  name: string;
  description: string;
}

@Component({
  selector: 'app-criar-perfil',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    CheckboxModule,
    BackButtonComponent,
    ButtonModule,
    DrawerModule,
    DialogModule,
    Toast,
    ConfirmDialogModule,
    ReactiveFormsModule,
    ToastModule,
  ],
  providers: [MessageService, GrupoPermissoesService, ConfirmationService],
  templateUrl: './criar-perfil.component.html',
  styleUrl: './criar-perfil.component.scss',
})
export class CriarPerfilComponent implements OnInit {
  messageService = inject(MessageService);
  grupoPermissoesService = inject(GrupoPermissoesService);
  confirmationService = inject(ConfirmationService);

  form!: FormGroup;
  searchTermCtrl = new FormControl<string>('', { nonNullable: true });

  roles: RoleDto[] = [];
  assignedRoles: RoleDto[] = [];
  filteredRoles: RoleDto[] = [];
  selectedRoleIds = new Set<string>();

  groupRolesLoaded = false;
  groupHasRoles = false;
  groupId: string | null = null;

  showPreviewProfile: boolean = false;

  constructor(private route: ActivatedRoute, private fb: FormBuilder) { }

  ngOnInit(): void {
    this.initForm();
    this.loadRouteData();
    this.loadRoles();
    this.searchTermCtrl.valueChanges.subscribe((v) => this.applyFilter(v));
  }

  private initForm(): void {
    this.form = this.fb.group({
      nome: [{ value: '', disabled: true }, Validators.required],
      id: [{ value: '', disabled: true }, Validators.required],
      descricao: [''],
    });
  }

  private loadRouteData(): void {
    const idPath = this.route.snapshot.paramMap.get('id');
    this.route.queryParamMap.subscribe((q) => {
      const nome = q.get('nome');
      const idQuery = q.get('id');
      this.groupId = idPath || idQuery;
      this.form.patchValue({
        id: this.groupId,
        nome: nome,
      });
      if (this.roles.length > 0 && this.groupId) {
        this.loadGroupAssignedRoles(this.groupId);
      }
    });
  }

  private loadRoles(): void {
    this.grupoPermissoesService.listAllRoles().subscribe({
      next: (response) => {
        const list = Array.isArray(response) ? response : response?.data ?? [];
        this.roles = list
          .filter((r: any) => r.description && r.description.trim() !== '')
          .map((r: any) => ({
            id: r.id,
            name: r.name,
            description: r.description,
          }));
        this.applyFilter();
        if (this.groupId) {
          this.loadGroupAssignedRoles(this.groupId);
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao carregar todas as roles.',
        });
      },
    });
  }

  private loadGroupAssignedRoles(groupId: string): void {
    this.groupRolesLoaded = false;
    this.groupHasRoles = false;

    this.grupoPermissoesService.listRoleForGroupID(groupId).subscribe({
      next: (resp) => {
        const dataArr = Array.isArray(resp) ? resp : resp?.data ?? [];

        const roleNames: string[] = dataArr.reduce((acc: string[], g: any) => {
          if (Array.isArray(g?.roles)) acc.push(...g.roles);
          return acc;
        }, []);

        if (roleNames.length) {
          this.groupHasRoles = true;

          const matchedIds = this.roles
            .filter(r => roleNames.includes(r.name))
            .map(r => r.id);

          this.selectedRoleIds = new Set(matchedIds);

          this.assignedRoles = this.roles.filter(r => roleNames.includes(r.name));

        } else {
          this.selectedRoleIds.clear();
          this.assignedRoles = [];
        }

        this.groupRolesLoaded = true;
      },
      error: () => {
        this.groupRolesLoaded = true;
        this.groupHasRoles = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao carregar permissões do grupo.',
        });
      },
    });
  }


  applyFilter(term?: string): void {
    const t = (term ?? this.searchTermCtrl.value ?? '').trim().toLowerCase();
    if (!t) {
      this.filteredRoles = [...this.roles];
      return;
    }
    this.filteredRoles = this.roles.filter(
      (r) =>
        r.description.toLowerCase().includes(t) ||
        r.name.toLowerCase().includes(t)
    );
  }

  isRoleSelected(id: string): boolean {
    return this.selectedRoleIds.has(id);
  }

  onRoleToggle(id: string, checked: boolean): void {
    if (checked) {
      this.selectedRoleIds.add(id);
    } else {
      this.selectedRoleIds.delete(id);
    }
  }

  get totalRoles(): number {
    return this.roles.length;
  }

  get totalSelected(): number {
    return this.selectedRoleIds.size;
  }

  get areAllSelected(): boolean {
    return this.totalSelected > 0 && this.totalSelected === this.totalRoles;
  }

  toggleSelectAll(): void {
    if (this.areAllSelected) {
      this.selectedRoleIds.clear();
    } else {
      this.selectedRoleIds = new Set(this.roles.map((r) => r.id));
    }
  }

  get selectedRoles(): RoleDto[] {
    return this.roles.filter((r) => this.selectedRoleIds.has(r.id));
  }

  get previewNome(): string {
    return this.form.get('nome')?.value || '';
  }

  get previewDescricao(): string {
    return this.form.get('descricao')?.value || '';
  }

  save(): void {
    if (this.form.invalid) {
      console.warn('Formulário inválido, não pode salvar.', this.form.errors);
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Por favor, preencha todos os campos obrigatórios.',
      });
      return;
    }

    const raw = this.form.getRawValue();
    const gid = this.groupId ?? raw.id;
    const roleIds = Array.from(this.selectedRoleIds);
    this.grupoPermissoesService.saveGroupRoles(gid, roleIds).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Permissões salvas com sucesso.',
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao salvar as permissões.',
        });
      },
    });
  }

  showModalRoles: boolean = false;
  showModalremoveRoles() {
    if (!this.groupId || this.selectedRoleIds.size === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'Nenhum grupo ou permissões selecionadas para remover.',
      });
      return;
    }
    this.showModalRoles = true;
  }

  trackByRole = (_: number, role: RoleDto) => role.id;

  confirmRemoveRole(role: RoleDto): void {
    this.confirmationService.confirm({
      message: `Tem certeza que deseja remover a permissão "${role.description}" deste grupo?`,
      header: 'Confirmar remoção',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.removeRole(role),
    });
  }

  private removeRole(role: RoleDto): void {
    if (!this.groupId) return;

    this.grupoPermissoesService.deleteRoleByGroupId(this.groupId, role.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: `Permissão "${role.description}" removida com sucesso.`,
        });
        this.loadGroupAssignedRoles(this.groupId!);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível remover a permissão.',
        });
      },
    });
  }

}
